import type { DeviceChannelDto, DeviceDto } from "@/lib/api/types";
import type { JsonObject } from "@/lib/api/types/json";

export type TelemetryThresholdSeverity = "critical" | "warning" | null;

export type ChannelThresholds = {
  min: number | null;
  max: number | null;
  warningLow: number | null;
  warningHigh: number | null;
};

function asJsonObject(value: unknown): JsonObject {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {};
}

function readNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function metadataChannelFor(device: DeviceDto | null | undefined, channel: DeviceChannelDto): JsonObject {
  const rawChannels = asJsonObject(device?.metadata).channels;
  if (!Array.isArray(rawChannels)) {
    return {};
  }

  for (const item of rawChannels) {
    const meta = asJsonObject(item);
    if (
      meta.id === channel.id ||
      meta.key === channel.key ||
      meta.channelKey === channel.key
    ) {
      return meta;
    }
  }

  return {};
}

export function getChannelThresholds(
  device: DeviceDto | null | undefined,
  channel: DeviceChannelDto,
): ChannelThresholds {
  const meta = metadataChannelFor(device, channel);
  return {
    min: channel.min_value ?? readNumber(meta.minValue),
    max: channel.max_value ?? readNumber(meta.maxValue),
    warningLow: readNumber(meta.thresholdWarningLow),
    warningHigh: readNumber(meta.thresholdWarningHigh),
  };
}

export function telemetryThresholdSeverity(
  value: unknown,
  thresholds: ChannelThresholds | null | undefined,
): TelemetryThresholdSeverity {
  const numeric = readNumber(value);
  if (numeric == null || !thresholds) {
    return null;
  }

  if (
    (thresholds.min != null && numeric <= thresholds.min) ||
    (thresholds.max != null && numeric >= thresholds.max)
  ) {
    return "critical";
  }

  if (
    (thresholds.warningLow != null && numeric <= thresholds.warningLow) ||
    (thresholds.warningHigh != null && numeric >= thresholds.warningHigh)
  ) {
    return "warning";
  }

  return null;
}

export function telemetrySeverityTextClass(severity: TelemetryThresholdSeverity): string {
  if (severity === "critical") {
    return "text-error";
  }
  if (severity === "warning") {
    return "text-warning";
  }
  return "";
}
